// Portal home page. Redesigned 2026-08-03: the old version was a single column of very large
// buttons that pushed most of the modules below the fold. Now it opens with the quote of the day
// and lays the modules out as a grid of cards, so everything a person can reach is on one screen.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { BarChart3, BriefcaseBusiness, FlaskConical, Factory, ArrowRight } from "lucide-react";
import XmasHero from './XmasHero';
import SignupCard from './SignupCard';
import XmasShower from './XmasShower';
import "./style.css";
import Header from './Header';
import useAuth from './useAuth';
import HomeDashboard from './HomeDashboard';
import HomeWorkload from './HomeWorkload';
import { BACKEND_URL } from './backendConfig';
import { VN_TZ } from './timeZone';

const greetingKeyFor = (hour) => {
    if (hour < 12) return 'home.morning';
    if (hour < 18) return 'home.afternoon';
    return 'home.evening';
};

// Everything on screen is pinned to Vietnam time, matching the rest of the portal.
// The greeting's date line in the reader's language (2026-10-21): English keeps "Tuesday, 21 October 2026"; the other
// three take their own way of writing a date ("Thứ Tư, 21 tháng 10, 2026", "2026年10月21日星期三").
const DATE_LOCALE = { ko: 'ko-KR', vi: 'vi-VN', zh: 'zh-CN' };
const nowInVietnam = (lang) => {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: VN_TZ, weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', hour12: false,
    }).formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const local = DATE_LOCALE[String(lang || '').slice(0, 2)];
    return {
        hour: Number.parseInt(parts.hour === '24' ? '0' : parts.hour, 10),
        dateLabel: local
            ? new Intl.DateTimeFormat(local, { timeZone: VN_TZ, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(now)
            : `${parts.weekday}, ${parts.day} ${parts.month} ${parts.year}`,
    };
};

function Home({ functions = [] }) {
    const navigate = useNavigate();
    const { name, firstName, activeRole, lastLogin } = useAuth();
    const { t, i18n } = useTranslation();
    /* Role-aware home (Jay 2026-08-17). A role with cards configured gets its own dashboard instead of
       the launcher grid; every other role keeps the launcher, which is what Jay asked for. Which
       dashboard follows the role picked in the header (Max, 2026-08-22), so the request is re-made when
       that changes. `home` stays null until the answer arrives — showing the launcher first and then
       swapping it for a dashboard would flash the wrong page at everybody. */
    const [home, setHome] = useState(null);
    /* Which Sales Rep an admin is looking AT — '' is every rep at once. Only the server decides
       whether that is allowed; this is just what was picked. */
    const [asRep, setAsRep] = useState('');
    /* The queue's Viewing picker carries a PORTAL USER id; asRep carries a sales_rep_id. One
       state for both would have scoped whichever dashboard read it first — Jay, 2026-09-16. */
    const [asUser, setAsUser] = useState('');
    /* Bumped to re-run the loader below. Changing the badly-late threshold changes what every card
       in that section counts, so nothing short of asking the server again is honest. */
    const [reloadKey, setReloadKey] = useState(0);
    useEffect(() => {
        let alive = true;
        const qs = new URLSearchParams();
        if (activeRole) qs.set('role', activeRole);
        if (asRep) qs.set('asRep', asRep);
        if (asUser) qs.set('asUser', asUser);
        const url = `${BACKEND_URL}/api/portal/home${qs.toString() ? `?${qs}` : ''}`;
        fetch(url, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : { layout: 'launcher' }))
            // A home page must render even when its extras fail, so any error falls back to the launcher.
            .catch(() => ({ layout: 'launcher' }))
            .then((d) => { if (alive) setHome(d); });
        return () => { alive = false; };
    }, [activeRole, asRep, asUser, reloadKey]);
    /* The server sends a section per role; anything published before 2026-09-14 sent one dashboard
       at the top level, so a tab left open across the deploy still renders. */
    const sections = home?.sections?.length ? home.sections
        : (home?.layout === 'dashboard' ? [home] : []);
    const showDashboard = home?.layout === 'dashboard'
        && sections.some((s) => !!(s.cards || []).length);
    const can = (key) => Array.isArray(functions) && functions.includes(key);
    const { hour, dateLabel } = nowInVietnam(i18n.language);

    // Same permission gates as the sidebar, so the home page can never offer a page that 403s.
    const modules = [
        {
            title: t('nav.report'), icon: BarChart3,
            items: [
                ...(can('product_sales_dashboard') ? [{ label: t('nav.productSalesDashboard'), path: '/product-sales-dashboard' }] : []),
                ...(can('sales_report_dashboard') ? [{ label: t('nav.salesReportDashboard'), path: '/sales-report-dashboard' }] : []),
                ...(can('demand_forecast_dashboard') ? [{ label: t('nav.demandForecastDashboard'), path: '/demand-forecast-dashboard' }] : []),
            ],
        },
        {
            title: t('nav.saleOperation'), icon: BriefcaseBusiness,
            items: [
                /* Straight to the Sales Op screen (2026-10-25, Max on the one-button "SOP Team" page: "bỏ step bấm nút này dc hem") —
           with the new screen on; production's older screen keeps its menu page until then. */
        ...(can('auto_jobs') ? [{ label: t('nav.autoJobs'), path: process.env.REACT_APP_SOP_WEEKLY === '1' && can('auto_jobs_sop_team') ? '/run-python-sop-team' : '/auto-jobs-menu' }] : []),
            ],
        },
        {
            title: t('nav.development'), icon: FlaskConical,
            items: [
                ...(can('sample_tracker') ? [{ label: t('nav.dashboard'), path: '/development/sample-tracker?tab=dash' }] : []),
                ...(can('project_tracker') ? [{ label: t('nav.projectTracker'), path: '/development/sample-tracker?tab=project' }] : []),
                ...(can('sample_tracker') ? [{ label: t('nav.sampleTracker'), path: '/development/sample-tracker?tab=sample' }] : []),
                ...(can('ticket_station') ? [{ label: t('nav.ticketStation'), path: '/development/sample-tracker?tab=tickets' }] : []),
            ],
        },
        {
            title: t('nav.production'), icon: Factory,
            items: [
                ...(can('nfc_portal') ? [{ label: t('nav.nfcTokenPortal'), path: '/production/nfc' }] : []),
            ],
        },
    ].filter((m) => m.items.length > 0);

    return (
        <div>
            <Header />
            <div className="content">
                <main className={`home-wrap${showDashboard ? ' wide' : ''}`}>
                    {/* the Christmas sky (XmasHero.jsx, season.js) — in the page always, shown by theme-christmas.css only in season */}
                    <XmasHero />
                    <XmasShower lastLogin={lastLogin} />
                    {/* a new person picks entity + team; a role waiting for approval says so (Jay #1160) — nothing for anybody else */}
                    <SignupCard />
                    <section className="home-greeting">
                        {/* The GIVEN name — Max, 2026-09-16. `name` is the login's UserName, which is
                            the given name for almost everybody but carries a surname for some, so the
                            page greeted SooBin Hong by her full name. Falls back to `name`, because a
                            row with no first name still has to be greeted as somebody. */}
                        <p className="home-hello">{t(greetingKeyFor(hour))}{(firstName || name) ? `, ${firstName || name}` : ''}</p>
                        <p className="home-date">{dateLabel}</p>
                    </section>


                    {/* One block per role the person holds — Jay, 2026-09-14: "a multi-role user
                        simply sees their role dashboards one below another". Picking a single role in
                        the header still narrows it to that one; the server decides which, and sends a
                        one-entry list when it is one. */}
                    {showDashboard && sections.map((section) => (
                        <HomeDashboard
                            key={section.role}
                            data={section}
                            stacked={sections.length > 1}
                            lang={i18n.language}
                            asRep={asRep}
                            onAsRep={setAsRep}
                            asUser={asUser}
                            onAsUser={setAsUser}
                            onReload={() => setReloadKey((k) => k + 1)}
                            onChanged={(rows) => setHome((h) => (h ? {
                                ...h,
                                sections: (h.sections || []).map((x) => (x.role === section.role
                                    ? { ...x, projectRows: rows } : x)),
                            } : h))}
                        />
                    ))}

                    {/* Only ever present when the viewer holds `workload_snapshot` — the server leaves
                        it out entirely for everybody else, so there is nothing here to hide. */}
                    {home?.workload && <HomeWorkload data={home.workload} />}

                    {!showDashboard && home !== null && <div className="home-grid">
                        {modules.map((module) => {
                            const Icon = module.icon;
                            return (
                                <section className="home-card" key={module.title}>
                                    <header className="home-card-head">
                                        <Icon size={20} strokeWidth={2.2} />
                                        <h2>{module.title}</h2>
                                    </header>
                                    <ul className="home-card-links">
                                        {module.items.map((item) => (
                                            <li key={item.path}>
                                                <button type="button" onClick={() => navigate(item.path)}>
                                                    <span>{item.label}</span>
                                                    <ArrowRight size={16} strokeWidth={2.2} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            );
                        })}
                    </div>}

                    {!showDashboard && modules.length === 0 && (
                        <p className="home-empty">
                            {t('home.noModules')}
                        </p>
                    )}
                </main>
            </div>
            <style jsx="true">{`
                .home-wrap {
                    max-width: 1080px;
                    margin: 0 auto;
                    padding: 28px 20px 64px;
                    display: flex;
                    flex-direction: column;
                    gap: 30px;
                }
                /* Jay Jung, 2026-09-14: "even maximized on a full screen they don't widen, so the
                   text inside gets cut off and I have to guess at it". 1080px is right for the
                   launcher — a column of module cards has no use for 2500px — but a dashboard card
                   holds project names, and at 1080 they were cut on every screen: measured at 1400,
                   1920 and 2560 the cards stayed 251px wide and 17 of the 23 names were ellipsed.
                   The dashboard therefore takes the width it is given; the card grid itself already
                   knows how to spread into it (auto-fit). */
                .home-wrap.wide { max-width: none; }
                .home-greeting {
                    display: flex;
                    align-items: baseline;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 8px;
                    border-bottom: 3px solid var(--portal-text);
                    padding-bottom: 12px;
                }
                .home-hello {
                    margin: 0;
                    font-size: 32px;
                    font-weight: 600;
                    color: var(--portal-text);
                }
                .home-date {
                    margin: 0;
                    font-size: 14px;
                    font-weight: 600;
                    letter-spacing: 0.04em;
                    color: var(--portal-text-muted);
                }
                .home-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 22px;
                }
                .home-card {
                    background: var(--portal-surface);
                    border: 3px solid var(--portal-text);
                    display: flex;
                    flex-direction: column;
                }
                .home-card-head {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 13px 18px;
                    border-bottom: 3px solid var(--portal-text);
                }
                .home-card-head h2 {
                    margin: 0;
                    font-size: 19px;
                    font-weight: 700;
                    letter-spacing: 0.02em;
                }
                .home-card-links {
                    margin: 0;
                    padding: 0;
                    list-style: none;
                    display: flex;
                    flex-direction: column;
                }
                .home-card-links > li + li { border-top: 1px solid var(--portal-border); }
                .home-card-links button {
                    appearance: none;
                    background: var(--portal-surface);
                    border: 0;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 14px 18px;
                    font-size: 15.5px;
                    font-weight: 500;
                    color: var(--portal-text);
                    text-align: left;
                    cursor: pointer;
                    transition: background 0.12s ease, padding-left 0.12s ease;
                }
                .home-card-links button svg { flex: none; opacity: 0.25; transition: opacity 0.12s ease; }
                .home-card-links button:hover {
                    background: var(--portal-text);
                    color: var(--portal-surface);
                    padding-left: 22px;
                }
                .home-card-links button:hover span { color: var(--portal-surface); }
                .home-card-links button:hover svg { opacity: 1; }
                .home-card-links button:focus-visible { outline: 3px solid #2E75B6; outline-offset: -3px; }
                .home-empty {
                    border: 2px dashed var(--portal-border);
                    padding: 22px;
                    text-align: center;
                    color: var(--portal-text-muted);
                }
                @media (max-width: 768px) {
                    .home-wrap { padding: 20px 14px 48px; gap: 24px; }
                    .home-hello { font-size: 25px; }
                    .home-grid { grid-template-columns: 1fr; gap: 18px; }
                }
            `}</style>
        </div>
    )
}

export default Home
